import { useMemo, useRef } from 'react';

import { isFedRamp } from '@atlassian/atl-context';
import { createPromptEditor } from '@atlassian/editor-ai-injected-editors/prompt-editor';
import { useLinkPickerEditorProps } from '@atlassian/link-picker-plugins/editor';
import {
	createPageEditorPluginAIOptions,
	createPageCommentEditorPluginAIOptions,
} from '@atlassian/editor-plugin-ai/ConfluencePrebuilt';
import type { AIProactivePluginConfig } from '@atlassian/editor-plugin-ai-proactive';

import { useSpaceId } from '@confluence/space-utils';
import { useSessionData } from '@confluence/session-data';

import { useLinkingFeatures } from '../components/EditorLinkingFeatures';

import { useMentionProvider } from './useMentionProvider';
import { useEditorPluginAIProactiveEnabled } from './useEditorPluginAIProactive';

export type Sentiment = 'good' | 'bad' | 'unknown';

type EditorPluginAIOptions = Parameters<typeof createPageEditorPluginAIOptions>[0];
type HandleFeedbackSubmission = NonNullable<EditorPluginAIOptions>['handleFeedbackSubmission'];
type HandleFeedbackSubmissionAIProactive = AIProactivePluginConfig['handleFeedbackSubmission'];

export type HandleFeedbackSubmissionArgs =
	| Parameters<NonNullable<HandleFeedbackSubmission>>[0]
	| Parameters<NonNullable<HandleFeedbackSubmissionAIProactive>>[0];

interface ConfluenceEditorPluginAIOptions {
	appearance?: 'full-page' | 'page-comment';
	/**
	 * Triggers when "thumbs up/down" feedback buttons are is clicked - probably
	 * want to start a survey (freeform field) at this point to collect any
	 * further details.
	 *
	 * Sentiment shape/details may change in future, we may expose that information
	 * more directly as explicit data shapes, but abstracting away to events
	 * for now to get an early iteration working.
	 */

	onFeedbackClicked?: (props: HandleFeedbackSubmissionArgs) => void;
	/**
	 * Triggers when the AI plugin enters a review state (response finished streaming)
	 */
	onReviewStateEntered?: () => void;
	/**
	 * Triggers when the AI plugin experience is complete, e.g. user has inserted
	 * content into the page, ending the AI experience.
	 */
	onExperienceComplete?: () => void;
	/**
	 * Opt in status of AI
	 */
	aiGlobalOptIn: EditorPluginAIOptions['aiGlobalOptIn'];

	AIButtonWrapper?: React.FC<{ children: React.ReactNode }>;
	/**
	 * Used to generate hash for pages channelID with use of Rovo agents.
	 */
	getChannelVariables?: EditorPluginAIOptions['getChannelVariables'];

	/**
	 * Triggered when the document has been changed by a Rovo agent.
	 */
	onDocChangeByAgent?: EditorPluginAIOptions['onDocChangeByAgent'];

	/**
	 * Triggered when the AI provider interaction starts.
	 */
	onAIProviderChanged?: EditorPluginAIOptions['onAIProviderChanged'];

	providers?: EditorPluginAIOptions['providers'];

	/**
	 * contentId (aka pageId or objectId) to use in audit log settings.
	 */
	contentId?: string;
}

const CARD_OPTIONS = { allowDatasource: true };

const defaultOptions: ConfluenceEditorPluginAIOptions = {
	appearance: 'full-page',
	onFeedbackClicked: () => {},
	onReviewStateEntered: () => {},
	onExperienceComplete: () => {},
	aiGlobalOptIn: { status: 'disabled', triggerOptInFlow: () => {} },
};

export const useIsEditorPluginAIEnabled = (): boolean => {
	const { isAdminHubAIEnabled } = useSessionData();
	return !isFedRamp() && isAdminHubAIEnabled;
};

export const useEditorPluginAIOptions = (
	options?: ConfluenceEditorPluginAIOptions,
): ReturnType<typeof createPageEditorPluginAIOptions> => {
	const optionsToUse = options || defaultOptions;

	const createEditorPluginAIOptions = useMemo(
		() =>
			optionsToUse.appearance === 'page-comment'
				? createPageCommentEditorPluginAIOptions
				: createPageEditorPluginAIOptions,
		[optionsToUse.appearance],
	);

	const optionsToUseRef = useRef(optionsToUse);
	optionsToUseRef.current = optionsToUse;

	const { cloudId } = useSessionData();
	const productConfig = useMemo(
		() => ({
			product: 'confluence',
			cloudId,
		}),
		[cloudId],
	);
	const linkPicker = useLinkPickerEditorProps(undefined, productConfig);
	const spaceId = useSpaceId();
	const mentionProvider = useMentionProvider(spaceId);

	const linkingProp = useLinkingFeatures(CARD_OPTIONS);

	const PromptEditor = useMemo(
		() =>
			createPromptEditor({
				linkPicker,
				enableLinks: true,
				linking: linkingProp.linking,
				mentionProvider,
				featureFlags: {},
			}),
		[linkingProp.linking, linkPicker, mentionProvider],
	);

	const editorPluginAIProactiveEnabled = useEditorPluginAIProactiveEnabled({
		aiGlobalOptInStatus: optionsToUse.aiGlobalOptIn.status,
	});

	return useMemo(
		() =>
			createEditorPluginAIOptions({
				aiGlobalOptIn: optionsToUseRef.current.aiGlobalOptIn,
				hidePrimaryToolbarButton: editorPluginAIProactiveEnabled,
				generativeAIApiUrl: `${window.location.origin}/gateway/api/editor-ai/v1/generative/ai`,
				handleFeedbackSubmission: async ({ sentiment, getAIExperience, editorAttributes }) => {
					try {
						// TODO: EDF-831: Fix and ensure types match with handleFeedbackSubmission
						// @ts-ignore
						optionsToUseRef.current.onFeedbackClicked?.({
							sentiment,
							getAIExperience,
							editorAttributes,
						});
						return { status: 'submitted' as const };
					} catch (err) {
						return { status: 'failed' as const };
					}
				},
				onExperienceEvent: {
					// Thumbs up and down are being deprecated and are no longer used.
					THUMBS_UP: () => {},
					THUMBS_DOWN: () => {},
					REVIEW_STATE_ENTERED: () => {
						optionsToUseRef.current.onReviewStateEntered?.();
					},
					EXPERIENCE_COMPLETE: () => {
						optionsToUseRef.current.onExperienceComplete?.();
					},
				},
				onDocChangeByAgent: (_agent) => {
					// TODO Integrate with confluence
				},
				onAIProviderChanged: (source, aiProvider) => {
					if (optionsToUseRef.current.onAIProviderChanged) {
						optionsToUseRef.current.onAIProviderChanged(source, aiProvider);
					}
				},
				getChannelVariables: optionsToUseRef.current.getChannelVariables,
				product: 'CONFLUENCE',
				PromptEditor,
				AIButtonWrapper: options?.AIButtonWrapper,
				providers: options?.providers,
				/**
				 * Need to audit logs only for confluence page as of now.
				 */
				auditLogSettings:
					optionsToUse.appearance !== 'page-comment' && optionsToUse.contentId
						? {
								enabled: true,
								eventData: {
									type: 'page',
									pageId: optionsToUse.contentId,
								},
							}
						: { enabled: false },
			}),
		[
			createEditorPluginAIOptions,
			PromptEditor,
			options?.AIButtonWrapper,
			options?.providers,
			optionsToUse.appearance,
			optionsToUse.contentId,
			editorPluginAIProactiveEnabled,
		],
	);
};
